import type { ConceptDataobject } from "@lr-notitia/notitia-engineer/schemas";
import type { Graph, Node, Link, Anchor, Spawn } from "@livereader/graphly-d3";
import { AnchorType, LinkType, LinkStrength } from "@livereader/graphly-d3";
import type { Terminology } from "@lr-notitia/notitia-dictionary/schemas";

import { useViewsStore } from "../../../store/views";
import { useGraphStore } from "../../../store/graph";
import { useGraphlyTemplatesStore, type DataobjectSchema } from "../../../store/graphly-templates";
import { useSymbolsStore } from "../../../store/symbols";
import { ConceptView } from "@lr-notitia/notitia-model-store/schemas";

let viewsStore: ReturnType<typeof useViewsStore>;
let graphStore: ReturnType<typeof useGraphStore>;
let graphlyTemplatesStore: ReturnType<typeof useGraphlyTemplatesStore>;
let symbolsStore: ReturnType<typeof useSymbolsStore>;

function init() {
	viewsStore = useViewsStore();
	graphStore = useGraphStore();
	graphlyTemplatesStore = useGraphlyTemplatesStore();
	symbolsStore = useSymbolsStore();
}

export function graph(viewId: string): Graph {
	if (!viewsStore || !graphStore) init();
	const view = graphStore.concept.view.byId(viewId);
	if (!view) return { nodes: [], links: [] };
	const nodes: Node[] = [];
	const links: Link[] = [];

	const dataobjects = dataobjectsInView(view.id);
	dataobjects.forEach((dataobject) => {
		nodes.push(dataobjectNode(dataobject, view));
		let hasALinks: Link[] = dataobject.hasA.map((l) => ({
			source: dataobject.id,
			target: l,
			type: LinkType.Dashed,
			directed: true,
			label: "has a",
			strength: LinkStrength.Loose,
			payload: {
				type: "has_a",
			},
		}));
		let composedByLinks: Link[] = dataobject.composedBy.map((l) => ({
			source: dataobject.id,
			target: l,
			type: LinkType.Solid,
			directed: true,
			label: "composed by",
			strength: LinkStrength.Loose,
			payload: {
				type: "composed_by",
			},
		}));
		links.push(...hasALinks);
		links.push(...composedByLinks);
		hasALinks = [];
		composedByLinks = [];
	});

	return { nodes, links };
}

export function parents(dataobjectId: string, depth = 0): string[] {
	const dataobject = graphStore.concept.dataobject.byId(dataobjectId);
	if (!dataobject) return [];
	const dataobjects = graphStore.concept.dataobjects
		.filter((d) => d.hasA.includes(dataobjectId) || d.composedBy.includes(dataobjectId))
		.map((p) => p.id);
	if (depth != 0) {
		dataobjects.forEach((d) => {
			let p = parents(d, depth - 1).filter((c) => !dataobjects.includes(c));
			dataobjects.push(...p);
			p = [];
		});
	}
	return dataobjects;
}
const visitedNodes = new Set<string>();

export function children(dataobjectId: string, depth = 0): string[] {
	if (visitedNodes.has(dataobjectId)) return [];
	visitedNodes.add(dataobjectId);
	const dataobject = graphStore.concept.dataobject.byId(dataobjectId);
	if (!dataobject) return [];
	const dataobjects = dataobject.hasA.concat(dataobject.composedBy);
	if (!dataobjects) return [];
	if (depth != 0) {
		dataobjects.forEach((d) => {
			let c = children(d, depth - 1).filter((c) => !dataobjects.includes(c));
			dataobjects.push(...c);
			c = [];
		});
	}
	return dataobjects;
}

function dataobjectsInView(viewId: string): ConceptDataobject[] {
	const view = graphStore.concept.view.byId(viewId);
	if (!view) return [];
	visitedNodes.clear();
	const dataobjects = [...view.entryNodes];
	dataobjects.forEach((d) => {
		let c = childrenInView(d, view).filter((c) => !dataobjects.includes(c));
		dataobjects.push(...c);
		c = [];
	});
	return dataobjects
		.filter((d, i, a) => a.indexOf(d) === i)
		.map((d) => graphStore.concept.dataobject.byId(d))
		.filter((d) => d !== undefined) as ConceptDataobject[];
}
function childrenInView(dataobjectId: string, view: ConceptView): string[] {
	if (!view.expandedNodes?.includes(dataobjectId)) return [];
	const dataobjects = children(dataobjectId);
	dataobjects.forEach((d) => {
		let c = childrenInView(d, view).filter((c) => !dataobjects.includes(c));
		dataobjects.push(...c);
		c = [];
	});
	return dataobjects;
}

function dataobjectNode(dataobject: ConceptDataobject, view: ConceptView): Node<DataobjectSchema> {
	return {
		id: dataobject.id,
		shape: {
			type: "dataobject",
			scale: view.entryNodes.includes(dataobject.id) ? 1.3 : 1,
			url: graphlyTemplatesStore.dataobjectShape,
		},
		...position(dataobject, view, parents(dataobject.id)[0]),
		payload: {
			title: title(dataobject.refersTo, dataobject.terminology || {}),
			stacked:
				!view.expandedNodes?.includes(dataobject.id) &&
				dataobject.hasA.concat(dataobject.composedBy).length > 0,
			emphasized: view.entryNodes.includes(dataobject.id),
			tags: graphStore.concept.attributes
				.filter((a) => dataobject.consistsOf.includes(a.id))
				.sort((a, b) => a.order - b.order)
				.map((a) => symbolsStore.term({ id: a.refersTo, terminology: a.terminology || {} })),
			type: "",
			color: dataobject.color,
		},
	};
}
function position(
	dataobject: ConceptDataobject,
	view: ConceptView,
	parent: string | undefined = undefined
): { x: number | undefined; y: number | undefined; anchor: Anchor | undefined; spawn: Spawn | undefined } {
	if (!viewsStore) init();
	const pos = viewsStore.nodePositions[view.id]?.[dataobject.id];
	if (pos) {
		return {
			x: pos.x,
			y: pos.y,
			anchor: { type: AnchorType.Soft, x: pos.x, y: pos.y },
			spawn: undefined,
		};
	} else if (!pos && parent) {
		return {
			x: undefined,
			y: undefined,
			anchor: undefined,
			spawn: { source: parent || "", distance: 500, angle: Math.random() * 360 },
		};
	} else {
		return {
			x: undefined,
			y: undefined,
			anchor: undefined,
			spawn: undefined,
		};
	}
}
function title(symbolId: string, localTerminology: Terminology): string {
	if (!symbolsStore) init();
	return symbolsStore.term({ id: symbolId, terminology: localTerminology });
}

export type ComposedDataobject = {
	id: string;
	dataobjects: ComposedDataobject[];
	attributes: string[];
};
export function composeDataobject(dataobjectId: string): ComposedDataobject {
	const dataobject = graphStore.concept.dataobject.byId(dataobjectId);
	if (!dataobject) return { id: "", dataobjects: [], attributes: [] };
	return {
		id: dataobject.id,
		attributes: [...dataobject.consistsOf],
		dataobjects: dataobject.composedBy.map((d) => composeDataobject(d)),
	};
}

export function composedAttributes(dataobjectId: string): string[] {
	const dataobject = graphStore.concept.dataobject.byId(dataobjectId);
	if (!dataobject) return [];
	return [
		...dataobject.consistsOf,
		...(children(dataobject.id, -1)
			.map((c) => graphStore.concept.dataobject.byId(c)?.consistsOf)
			.flat()
			.filter((c) => c) as string[]),
	];
}
